/**
 * Admin — notice moderation via the shared ModerationScreen pattern:
 * kind label + status, pin/unpin and deactivate/reactivate PATCH actions
 * (optimistic, reverting on error). Wired: GET/PATCH /api/admin/notices.
 * Port of screens-admin.jsx AdminNotices.
 */
import { router } from 'expo-router';
import React from 'react';
import { View } from 'react-native';

import { space } from '@bero/tokens';
import { bool, fmtWhen, str, useGateList } from '@/components/gate/api';
import { ModerationScreen, SmallButton } from '@/features/account-admin/bits';
import { api } from '@/lib/client';
import { useTheme } from '@/theme';
import { AppText, Card, Pill } from '@/ui';

type Row = { id: string; title: string; kind: string; label: string; when: string; pinned: boolean; active: boolean };

function toRow(n: unknown, i: number): Row {
  const kind = (str(n, 'kind', 'priority', 'category') ?? 'general').toLowerCase();
  return {
    id: str(n, 'id') ?? `n${i}`,
    title: str(n, 'title') ?? 'Notice',
    kind,
    label: str(n, 'label') ?? kind.toUpperCase(),
    when: fmtWhen(str(n, 'createdAt', 'postedAt'), str(n, 'when')),
    pinned: bool(n, 'pinned', 'isPinned') ?? false,
    active: bool(n, 'active', 'isActive') ?? true,
  };
}

export default function AdminNoticesScreen() {
  const { colors } = useTheme();
  const { items, setItems, state } = useGateList<unknown>('/api/admin/notices', 'notices');
  const rows = items.map(toRow);
  const [notice, setNotice] = React.useState<string | null>(null);

  const labelColor: Record<string, string> = {
    urgent: colors.danger500, event: colors.accent700, committee: colors.primary700, general: colors.textTertiary,
  };

  const patchRow = (id: string, patch: Record<string, boolean>, failMsg: string) => {
    const prev = items;
    setNotice(null);
    setItems(prev.map((n, i) => (toRow(n, i).id === id ? { ...(n as object), ...patch } : n))); // optimistic
    api.patch<unknown>(`/api/admin/notices/${id}`, patch).then((body) => {
      if ((body as { needsApproval?: boolean })?.needsApproval) {
        setItems(prev); // not done yet — dual approval queued it
        setNotice('Sent for a second approval — track it under Pending actions.');
      }
    }).catch(() => {
      setItems(prev); // revert
      setNotice(failMsg);
    });
  };

  return (
    <ModerationScreen
      title="Notices"
      createLabel="New notice"
      onCreate={() => router.push('/admin/create-notice')}
      state={state}
      count={rows.length}
      emptyIcon="megaphone"
      emptyTitle="No notices yet"
      emptyBody="Publish a notice to reach every resident. Tap + to create one."
      notice={notice}
    >
      {rows.map((n) => (
        <Card key={n.id} style={!n.active ? { opacity: 0.6 } : undefined}>
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: space.s2 }}>
            <AppText variant="label" style={{ color: labelColor[n.kind] ?? colors.textTertiary }}>{n.label}</AppText>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s2 }}>
              {!n.active ? <Pill status="pending" label="INACTIVE" /> : n.pinned ? <Pill status="approved" label="PINNED" /> : null}
              {n.when ? <AppText variant="bodySm" tone="tertiary">{n.when}</AppText> : null}
            </View>
          </View>
          <AppText variant="bodyMd" weight="500" style={{ marginTop: 6 }}>{n.title}</AppText>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s2, marginTop: space.s3 }}>
            <SmallButton
              label={n.pinned ? 'Unpin' : 'Pin'}
              icon="pin"
              active={n.pinned}
              disabled={!n.active}
              onPress={() => patchRow(n.id, { pinned: !n.pinned }, 'Could not update the pin. Try again.')}
            />
            <View style={{ flex: 1 }} />
            <SmallButton
              label={n.active ? 'Deactivate' : 'Reactivate'}
              danger={n.active}
              onPress={() => patchRow(n.id, { isActive: !n.active }, 'Could not update the notice. Try again.')}
            />
          </View>
        </Card>
      ))}
    </ModerationScreen>
  );
}
